import LoadingScreen from 'components/common/LoadingScreen';
import usePersistState from 'hooks/usePersistState';
import browserStorage from 'store';
import PropTypes from 'prop-types';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { matchRoutes, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import paths from 'routes/paths';
import { AbilityContext } from 'routes/permissions/Can';
import defineAbilityFor from 'routes/permissions/ability';
import { supabase, authHelpers } from '../lib/supabase';

/* -------------------------------------------------------------------------- */
/*                                  CONSTANTS                                */
/* -------------------------------------------------------------------------- */
export const ROLE_ORGANIZATION_ADMIN = 'Organisation Admin';
export const ROLE_BRANCH_STAFF = 'Staff';
export const ROLE_BRANCH_ADMIN = 'Branch Admin';
export const ROLE_GROUND_TEAM = 'Ground Team';
export const ROLE_GROUNDS_TEAM_MANAGER = 'Grounds Team Manager';

export const rolesMap = {
  organisationAdmin: {
    id: 'aa55dc52-ba98-4a23-9304-225e1d2708c4',
    name: ROLE_ORGANIZATION_ADMIN
  },
  staff: {
    id: '2c0a19f2-5819-4ddf-8c7d-d2daac1ff587',
    name: ROLE_BRANCH_STAFF
  },
  admin: {
    id: '95410e85-fc04-4c68-995c-e6204a54cf44',
    name: ROLE_BRANCH_ADMIN
  },
  groundTeam: {
    id: 'b8f5e9a2-3c7d-4e1f-9a2b-6d8c4f0e1a3b',
    name: ROLE_GROUND_TEAM
  },
  groundsTeamManager: {
    id: 'c8e7d6b5-4f3a-4e8b-9d2c-1a3b5c7e9f0d',
    name: ROLE_GROUNDS_TEAM_MANAGER
  }
};

/* -------------------------------------------------------------------------- */
/*                                  CONTEXTS                                 */
/* -------------------------------------------------------------------------- */

const AuthContext = createContext({
  is_main: false,
  organization: null,
  user: null,
  selectedBranchId: null,
  selectedUserRole: null,
  isAdmin: true,
  isOrganizationAdmin: false,
  isGroundsTeamManager: false,
  login: () => {},
  logout: () => {},
  registerOwner: () => {},
  loginWithMfaCheck: () => {},
  verifyMfaLogin: () => {},
  isAuthenticated: false,
  isInitialized: false,
  setOrganization: () => {},
  selectBranch: () => {},
  selectAdminBranch: () => {},
  removeBranchSelection: () => {},
  ability: null,
  initializeUser: () => {}
});

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within a AuthContextProvider');
  }
  return context;
};

/* -------------------------------------------------------------------------- */
/*                             AUTH PROVIDER                                 */
/* -------------------------------------------------------------------------- */

export const AuthContextProvider = ({ children }) => {
  const [organization, setOrganization] = useState();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [userRole, setUserRole] = usePersistState('userRole', {
    selectedBranchId: null,
    selectedUserRole: null,
    isAdmin: false,
    isOrganizationAdmin: false,
    isGroundsTeamManager: false,
    permissions: null,
    role: null,
    is_main: false
  });

  const {
    selectedBranchId,
    selectedUserRole,
    isAdmin,
    isOrganizationAdmin,
    isGroundsTeamManager,
    permissions,
    role,
    is_main,
    isAllBranches
  } = userRole;

  const [isInitialized, setIsInitialized] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Ref to suppress auth events during MFA check
  const skipAuthEventsRef = useRef(false);

  // Ref to track auth state for visibility change handler (avoids stale closure)
  const isAuthenticatedRef = useRef(false);

  // Initialize user from Supabase
  const initializeUser = async () => {
    try {
      const { user, error } = await authHelpers.getUser();

      if (error || !user) {
        setIsAuthenticated(false);
        setIsInitialized(true);
        setLoading(false);
        return {};
      }

      // Fetch user data from database with relations
      const { data: dbUser, error: dbError } = await supabase
        .from('users')
        .select(
          `
          *,
          organisation:organisation_id (
            id,
            organisation_name,
            organisation_email,
            logo,
            charity_number,
            city,
            country,
            address_1,
            address_2,
            post_code,
            currency_id,
            stripe_key
          ),
          users_roles (
            id,
            branch_id,
            organisation_id,
            role_id,
            is_active,
            permissions,
            role:role_id (
              name
            ),
            branch:branch_id (
              address,
              city,
              created_at,
              currency_id,
              id,
              location_id,
              logo,
              name,
              organisation_id,
              phone,
              is_main
            )
          )
        `
        )
        .eq('id', user.id)
        .single();

      if (dbError) {
        console.error('Error fetching user data:', dbError);

        // If we're on reset password page, don't logout - just mark as not fully initialized
        if (location.pathname === paths.resetPassword) {
          setIsAuthenticated(true); // Keep session for password reset
          setIsInitialized(true);
          setLoading(false);
          return {};
        }

        // For other errors, mark as not authenticated and initialized
        toast.error('Error loading user data');
        setIsAuthenticated(false);
        setIsInitialized(true);
        setLoading(false);
        return {};
      }

      setUserData(dbUser);
      const organization = dbUser.organisation;
      let branches = [];
      let userBranchRole = null;

      const isOrganizationAdmin = dbUser.users_roles.some(
        userRole =>
          userRole.role_id === rolesMap?.organisationAdmin?.id &&
          userRole.branch_id === null &&
          userRole.organisation_id === organization?.id
      );

      if (isOrganizationAdmin) {
        setUserRole(prev => ({
          ...prev,
          isAdmin: isOrganizationAdmin,
          isOrganizationAdmin,
          isGroundsTeamManager: false,
          role: ROLE_ORGANIZATION_ADMIN
        }));

        // Fetch branches for org admin
        if (organization?.id) {
          const { data: branchData } = await supabase
            .from('branches')
            .select('*')
            .eq('organisation_id', organization.id);
          branches = branchData || [];
        }
      } else {
        // For branch admins/staff/ground team. A user can belong to multiple
        // branches (one users_roles row per branch). Honour a previously
        // selected branch (e.g. chosen via the header switcher) if it's still
        // one of their memberships; otherwise default to their main branch, or
        // the first one. Permissions are per-branch, so we read them from the
        // selected membership row.
        const branchRoles = dbUser.users_roles.filter(
          role => role.is_active && role.branch_id
        );
        // On a cold load / post-switch reload, the chosen branch lives in
        // persisted storage. The `selectedBranchId` from state can still be
        // null at this point because usePersistState only restores it in a
        // mount effect, which may not have run before initializeUser fires.
        // Read it straight from storage so a branch user keeps the branch they
        // switched to instead of snapping back to their main branch (this is
        // why the header branch switcher appeared to do nothing for them).
        const persistedBranchId =
          browserStorage.get('userRole')?.selectedBranchId || selectedBranchId;
        userBranchRole =
          branchRoles.find(role => role.branch_id === persistedBranchId) ||
          branchRoles.find(role => role.branch?.is_main) ||
          branchRoles[0];

        // Role and permissions are per-branch, so derive them from the selected
        // membership row (not org-wide) - mirrors selectBranch. This keeps a
        // user who is, say, Branch Admin in one branch but Staff in another
        // from getting admin access in the wrong branch on cold load.
        const selectedRoleId = userBranchRole?.role_id;
        const isSelectedBranchAdmin = selectedRoleId === rolesMap.admin.id;
        const isSelectedGroundsTeamManager =
          selectedRoleId === rolesMap.groundsTeamManager.id;
        const isSelectedGroundTeam = selectedRoleId === rolesMap.groundTeam.id;
        const selectedRole = isSelectedBranchAdmin
          ? ROLE_BRANCH_ADMIN
          : isSelectedGroundsTeamManager
          ? ROLE_GROUNDS_TEAM_MANAGER
          : isSelectedGroundTeam
          ? ROLE_GROUND_TEAM
          : ROLE_BRANCH_STAFF;

        setUserRole(prev => ({
          ...prev,
          isAdmin: isSelectedBranchAdmin,
          isOrganizationAdmin,
          isGroundsTeamManager: isSelectedGroundsTeamManager,
          role: selectedRole,
          permissions: userBranchRole?.permissions,
          selectedBranchId: userBranchRole?.branch_id,
          selectedUserRole: userBranchRole,
          is_main: userBranchRole?.branch?.is_main
        }));
      }

      setOrganization(organization);

      // Check email verification
      const matches = matchRoutes(
        [{ path: paths.verifyEmail }],
        location.pathname
      );

      if (organization && !dbUser.email_verified_at && !matches) {
        navigate(paths.verifyEmailAlert);
      }
      if (organization && dbUser.email_verified_at && matches) {
        navigate(paths.dashboard);
      }

      setIsAuthenticated(true);
      setIsInitialized(true);
      setLoading(false);

      // No need to call invalidateQueries() here - React Query will automatically
      // fetch when component queries flip from enabled:false to enabled:true
      // (their `enabled` depends on isInitialized && organization?.id).
      // Calling invalidateQueries() here races with React state batching and
      // fires against still-disabled queries, causing the stale-data bug.

      return { organization, branches, userBranchRole };
    } catch (error) {
      console.error('Initialize user error:', error);
      setIsAuthenticated(false);
      setIsInitialized(true);
      setLoading(false);
      return {};
    }
  };

  // Listen to auth changes
  useEffect(() => {
    let mounted = true;

    // Listen for auth changes - this handles INITIAL_SESSION which fires when
    // Supabase has restored the session from localStorage
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      // INITIAL_SESSION fires when Supabase has finished loading session from storage
      // This is the correct time to initialize the user
      if (event === 'INITIAL_SESSION') {
        if (session) {
          // Password recovery links land on /reset-password with a fresh
          // recovery session. Running initializeUser() here would fetch
          // user data and navigate the user away (to dashboard or the
          // verify-email-alert) before they can submit a new password.
          // Let PASSWORD_RECOVERY (or the page itself) drive the state.
          if (location.pathname === paths.resetPassword) {
            setIsAuthenticated(true);
            setIsInitialized(true);
            setLoading(false);
            return;
          }
          // Defer out of the auth callback. Supabase holds an internal lock
          // while onAuthStateChange callbacks run; awaiting a Supabase query
          // in here deadlocks when a token refresh is pending (e.g. returning
          // from Stripe checkout after a few minutes), leaving the app stuck
          // on the boot loading screen forever.
          setTimeout(() => {
            initializeUser();
          }, 0);
        } else {
          // No session - user is not logged in
          setIsAuthenticated(false);
          setIsInitialized(true);
          setLoading(false);
        }
        return;
      }

      // Handle password recovery - don't initialize, just let the reset password page handle it
      if (event === 'PASSWORD_RECOVERY') {
        setIsAuthenticated(true);
        setIsInitialized(true);
        setLoading(false);
        return;
      }

      if (event === 'SIGNED_IN') {
        if (skipAuthEventsRef.current) return;
        // Skip re-initialization if already authenticated (e.g. spurious
        // SIGNED_IN fired during a token refresh). Without this guard the
        // org-admin flow re-fetches branches/user data mid-session, which
        // causes stale state and forces a manual page refresh.
        if (isAuthenticatedRef.current) return;
        // Deferred for the same lock/deadlock reason as INITIAL_SESSION above.
        setTimeout(() => {
          initializeUser();
        }, 0);
      } else if (event === 'SIGNED_OUT') {
        setIsAuthenticated(false);
        setUserData(null);
        setOrganization(null);
        setIsInitialized(true);
        setLoading(false);
      } else if (event === 'TOKEN_REFRESHED' && session) {
        // Session was refreshed, user is still authenticated.
        // Supabase client automatically uses the new token for subsequent
        // requests, so no need to invalidate queries - the data hasn't changed.
        setIsAuthenticated(true);
      }
    });

    // Handle visibility change - refresh session when returning to tab
    const handleVisibilityChange = async () => {
      // Use ref to avoid stale closure issue with isAuthenticated state
      if (document.visibilityState === 'visible' && isAuthenticatedRef.current) {
        try {
          // Check if session is still valid when returning to the tab
          const {
            data: { session }
          } = await supabase.auth.getSession();
          if (!session) {
            // Session expired, force logout
            logout({ sessionOut: true });
          }
          // If session is valid, do nothing - Supabase handles token refresh
          // automatically and React Query will refetch based on staleTime
          // when components re-render. No need to invalidate all queries.
        } catch (error) {
          console.error(
            'Error refreshing session on visibility change:',
            error
          );
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mounted = false;
      subscription?.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the auth ref in sync with state (for visibility change handler)
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  // No need for a useEffect to invalidateQueries when auth becomes ready.
  // React Query queries have `enabled: isInitialized && !!organization?.id`
  // and will automatically fetch when these values change from false to true.

  // Login
  const login = async ({ email, password }) => {
    try {
      const { error } = await authHelpers.signIn({ email, password });

      if (error) {
        toast.error(error.message);
        return;
      }

      // Initialize user will be called by onAuthStateChange
      const { organization, branches, userBranchRole } = await initializeUser();

      if (organization) {
        if (branches && branches.length > 0) {
          const firstBranch = branches.find(b => b.is_main) || branches[0];
          selectAdminBranch({
            selectedBranchId: firstBranch.id,
            is_main: firstBranch.is_main
          });
          navigate(paths.dashboard);
        } else if (userBranchRole) {
          selectBranch({
            userRole: userBranchRole,
            is_main: userBranchRole.branch?.is_main
          });
          navigate(paths.dashboard);
        } else {
          navigate(paths.dashboard);
        }
      } else {
        navigate(paths.registerBusinessInfo);
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('An error occurred during login');
    }
  };

  // Login with MFA check - returns mfaRequired if user has TOTP enrolled
  const loginWithMfaCheck = async ({ email, password }) => {
    try {
      // Suppress auth events until we know if MFA is required
      skipAuthEventsRef.current = true;

      const { error: signInError } = await authHelpers.signIn({
        email,
        password
      });

      if (signInError) {
        skipAuthEventsRef.current = false;
        return { success: false, error: signInError.message };
      }

      // Check if MFA is required
      const { data: mfaData, error: mfaError } =
        await authHelpers.getMfaAuthLevel();

      console.log('MFA Auth Level:', mfaData);

      if (mfaError) {
        console.error('MFA check error:', mfaError);
        // Continue without MFA if check fails
      }

      // Get the user's MFA factors
      const { data: factors, error: factorsError } =
        await authHelpers.getMfaFactors();
      console.log('MFA Factors:', factors, factorsError);

      const verifiedFactor = factors?.totp?.find(f => f.status === 'verified');

      // If user has MFA enrolled and needs to verify (AAL1 -> AAL2)
      if (
        mfaData?.nextLevel === 'aal2' &&
        mfaData?.currentLevel === 'aal1' &&
        verifiedFactor
      ) {
        console.log('MFA Required, factor:', verifiedFactor.id);
        // Keep skipAuthEventsRef true - we'll handle auth after MFA verification
        return {
          success: true,
          mfaRequired: true,
          mfaSetupRequired: false,
          factorId: verifiedFactor.id
        };
      }

      // If no MFA enrolled, require setup
      if (!verifiedFactor) {
        console.log('MFA setup required');
        // Keep skipAuthEventsRef true - we'll handle auth after MFA setup
        return {
          success: true,
          mfaRequired: false,
          mfaSetupRequired: true
        };
      }

      console.log('No MFA required, proceeding with login');
      skipAuthEventsRef.current = false;

      // No MFA required - complete login
      const { organization, branches, userBranchRole } =
        await initializeUser();
      handlePostLoginNavigation({ organization, branches, userBranchRole });

      return { success: true, mfaRequired: false };
    } catch (error) {
      skipAuthEventsRef.current = false;
      console.error('Login error:', error);
      return { success: false, error: 'An error occurred during login' };
    }
  };

  // Verify TOTP code and complete MFA login
  const verifyMfaLogin = async ({ factorId, code }) => {
    try {
      const { error } = await authHelpers.challengeAndVerifyMfa({
        factorId,
        code
      });

      if (error) {
        return { success: false, error: error.message };
      }

      // MFA verified - allow auth events again and complete login
      skipAuthEventsRef.current = false;

      const { organization, branches, userBranchRole } =
        await initializeUser();
      handlePostLoginNavigation({ organization, branches, userBranchRole });

      return { success: true };
    } catch (error) {
      skipAuthEventsRef.current = false;
      console.error('MFA verification error:', error);
      return { success: false, error: 'An error occurred during verification' };
    }
  };

  // Helper for post-login navigation
  const handlePostLoginNavigation = ({
    organization,
    branches,
    userBranchRole
  }) => {
    if (organization) {
      if (branches && branches.length > 0) {
        const firstBranch = branches.find(b => b.is_main) || branches[0];
        selectAdminBranch({
          selectedBranchId: firstBranch.id,
          is_main: firstBranch.is_main
        });
        navigate(paths.dashboard);
      } else if (userBranchRole) {
        selectBranch({
          userRole: userBranchRole,
          is_main: userBranchRole.branch?.is_main
        });
        navigate(paths.dashboard);
      } else {
        navigate(paths.dashboard);
      }
    } else {
      navigate(paths.registerBusinessInfo);
    }
  };

  // Register owner
  const registerOwner = async ({
    title,
    firstName,
    lastName,
    phoneNumber,
    email,
    password
  }) => {
    try {
      const { error } = await authHelpers.signUp({
        email,
        password,
        firstName,
        lastName,
        title,
        phone: phoneNumber
      });

      if (error) {
        toast.error(error.message);
        return;
      }

      toast.success(
        'Registration successful! Please check your email to verify your account.'
      );
      navigate(paths.login);
    } catch (error) {
      console.error('Register error:', error);
      toast.error('An error occurred during registration');
    }
  };

  // Logout
  const logout = async ({ sessionOut } = {}) => {
    try {
      await authHelpers.signOut();

      // Clear all persisted state
      setUserRole({
        selectedBranchId: null,
        selectedUserRole: null,
        isAdmin: false,
        isOrganizationAdmin: false,
        permissions: null,
        role: null,
        is_main: false
      });

      setIsAuthenticated(false);
      setUserData(null);
      setOrganization(null);

      // Force a hard reload to clear all cached state
      if (sessionOut === true) {
        window.location.href = paths.sessionOut;
      } else {
        window.location.href = paths.login;
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, force logout
      window.location.href = paths.login;
    }
  };

  // Branch selection helpers
  // When branch changes, queries that include branchId in their queryKey will
  // automatically refetch because React Query treats a new queryKey as a new query.
  // We only need to remove stale cached data for the old branch.
  const selectAdminBranch = ({ selectedBranchId, is_main, isAllBranches }) => {
    setUserRole(prev => ({
      ...prev,
      selectedBranchId,
      is_main,
      isAllBranches: isAllBranches || false
    }));
    // Remove all cached query data so components fetch fresh for the new branch.
    // removeQueries() clears the cache without triggering refetches on unmounted
    // queries, unlike invalidateQueries() which refetches everything.
    queryClient.removeQueries();
  };

  const selectBranch = ({ userRole, is_main }) => {
    if (!userRole) {
      setUserRole({
        selectedBranchId: null,
        selectedUserRole: null,
        isAdmin: false,
        isOrganizationAdmin: false,
        isGroundsTeamManager: false,
        permissions: null,
        role: null,
        is_main: is_main
      });
      queryClient.removeQueries();
      return;
    }
    const isBranchAdmin = userRole.role_id === rolesMap.admin.id;
    const isGroundTeam = userRole.role_id === rolesMap.groundTeam.id;
    const isGroundsTeamManager =
      userRole.role_id === rolesMap.groundsTeamManager.id;

    // Determine role based on role_id
    const getUserRoleType = () => {
      if (isBranchAdmin) return ROLE_BRANCH_ADMIN;
      if (isGroundsTeamManager) return ROLE_GROUNDS_TEAM_MANAGER;
      if (isGroundTeam) return ROLE_GROUND_TEAM;
      return ROLE_BRANCH_STAFF;
    };

    setUserRole({
      selectedBranchId: userRole.branch_id,
      selectedUserRole: userRole,
      isAdmin: isBranchAdmin,
      isOrganizationAdmin: false,
      isGroundsTeamManager,
      permissions: userRole.permissions,
      role: getUserRoleType(),
      is_main: userRole.is_main
    });
    queryClient.removeQueries();
  };

  const removeBranchSelection = () => {
    setUserRole(prev => ({
      ...prev,
      selectedBranchId: null,
      selectedUserRole: null,
      isAdmin: isOrganizationAdmin,
      permissions: null
    }));
    queryClient.removeQueries();
  };

  const ability = useMemo(() => {
    return defineAbilityFor(role, permissions);
  }, [permissions, role]);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        organization,
        user: userData ?? {},
        selectedBranchId,
        selectedUserRole,
        isAdmin,
        isOrganizationAdmin,
        isGroundsTeamManager,
        is_main,
        isAllBranches,
        login,
        logout,
        registerOwner,
        loginWithMfaCheck,
        verifyMfaLogin,
        isAuthenticated,
        isInitialized,
        setOrganization,
        selectBranch,
        selectAdminBranch,
        removeBranchSelection,
        ability,
        initializeUser
      }}
    >
      <AbilityContext.Provider value={ability}>
        {children}
      </AbilityContext.Provider>
    </AuthContext.Provider>
  );
};

AuthContextProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AuthContext;
